import { previewSummary } from './columns';
import {
    contrastNote,
    cssFontFamily,
    plural,
    readableTextOn,
    shortUrl,
    swatchHex,
    text,
} from './report-values';

import type { FinalRecord } from './final-report';
import type {
    BrandSpecimen,
    CellStatus,
    MappingRow,
    ModuleReportView,
    Observation,
    ValueCell,
} from './module-report-types';
import type { ClientSheet } from './report-sections';

/**
 * Branding's page in the final report. It reads the record columns the
 * Branding migrator declares (`colorSfButtons`, `colorV2`, `fontSf`, …) and the
 * warning codes `migrate()` raises. A key that is missing or of another type
 * degrades to a dash, never to a broken sheet: the report must still render
 * when the migrator's columns move.
 */

const FONT_NOT_APPLIED = 'BRANDING_FONT_NOT_APPLIED';
const FONT_NOT_AVAILABLE = 'BRANDING_FONT_NOT_AVAILABLE';
const LOGO_NOT_MIGRATED = 'BRANDING_LOGO_NOT_MIGRATED';
const COLOR_NOT_MIGRATED = 'BRANDING_COLOR_NOT_MIGRATED';

const TOPICS: Readonly<Record<string, string>> = {
    [FONT_NOT_APPLIED]: 'the typeface',
    [FONT_NOT_AVAILABLE]: 'the typeface',
    [LOGO_NOT_MIGRATED]: 'the logo',
    [COLOR_NOT_MIGRATED]: 'the colour',
    BRANDING_MULTIPLE_PRIMARY_COMPANIES: 'duplicate primary records',
    BRANDING_RETIRED_LOGO_ASSETS: 'retired logo assets',
};

const OK: CellStatus = { label: 'ok', tone: 'success' };

function warned(record: FinalRecord, code: string): boolean {
    return (record.tracking?.warnings ?? []).some((warning) => warning.code === code);
}

/**
 * Verdicts that are not a problem: Salesforce simply had nothing to carry
 * (`empty` typeface, `default_sf` colour), so V2 keeps its own default.
 */
const NEUTRAL_VERDICT: Readonly<Record<string, string>> = {
    empty: 'none in SF',
    default_sf: 'SF default',
};

/** A runner verdict (`ok`, `not_legible`…) as a status chip. */
function verdict(reason: string | null): CellStatus {
    if (reason === null) return { label: '—', tone: 'neutral' };
    if (reason === 'ok') return OK;
    const neutral = NEUTRAL_VERDICT[reason];
    if (neutral !== undefined) return { label: neutral, tone: 'neutral' };
    return { label: reason.replace(/_/g, ' '), tone: 'warning' };
}

function colourCell(value: unknown) {
    const swatch = swatchHex(value);
    return { text: swatch ?? text(value), mono: true, swatch };
}

const STORED: CellStatus = { label: 'stored', tone: 'success' };

function logoStatus(record: FinalRecord, reason: string | null): CellStatus {
    if (warned(record, LOGO_NOT_MIGRATED)) return { label: 'not migrated', tone: 'warning' };
    if (reason === null) return { label: '—', tone: 'neutral' };
    if (reason.startsWith('unsupported')) return { label: 'unsupported', tone: 'warning' };
    if (reason.startsWith('none')) return { label: 'no logo', tone: 'neutral' };
    // `to_download` on a record the run migrated without a logo warning: stored.
    if (record.tracking?.status === 'Migrated') return STORED;
    return { label: reason.replace(/_/g, ' '), tone: 'info' };
}

/** What V2 holds for the logo: "Stored in Files" with the probe once it is there, else nothing. */
function logoV2(record: FinalRecord, status: CellStatus): ValueCell {
    if (status !== STORED) return { text: null, placeholder: '—' };
    return { text: 'Stored in Files', note: previewSummary(record.preview) };
}

export function brandingMappingRows(record: FinalRecord): MappingRow[] {
    const c = record.columns;
    const fontNotApplied = warned(record, FONT_NOT_APPLIED);
    const logo = logoStatus(record, text(c.logoReason));
    const logoUrl = text(c.logoSfUrl);
    return [
        {
            attribute: 'Button colour',
            salesforce: colourCell(c.colorSfButtons),
            veevart: { ...colourCell(c.colorV2), note: contrastNote(c.colorRatio) },
            status: warned(record, COLOR_NOT_MIGRATED)
                ? { label: 'default used', tone: 'warning' }
                : verdict(text(c.colorReason)),
        },
        {
            attribute: 'Payment colour',
            salesforce: colourCell(c.colorSfPay),
            veevart: { text: null, placeholder: 'No V2 field' },
            status:
                text(c.colorSfPay) === null
                    ? { label: '—', tone: 'neutral' }
                    : { label: 'SF only', tone: 'neutral' },
        },
        {
            attribute: 'Shape',
            salesforce: {
                text: text(c.radiusSf) === null ? null : `radius ${String(text(c.radiusSf))}`,
                mono: true,
            },
            veevart: { text: text(c.shapeV2), mono: true },
            status: text(c.shapeV2) === null ? { label: '—', tone: 'neutral' } : OK,
        },
        {
            attribute: 'Typeface',
            salesforce: { text: text(c.fontSf) },
            veevart: {
                text: text(c.fontV2),
                placeholder: 'Default typeface',
                note: fontNotApplied ? 'Mapped, but not applied in this run' : null,
            },
            status: fontNotApplied
                ? { label: 'not applied', tone: 'warning' }
                : verdict(text(c.fontReason)),
        },
        {
            attribute: 'Logo',
            // The URL is long and says little past its host and file: the full
            // one stays on hover and in the downloads.
            salesforce: {
                text: logoUrl === null ? null : shortUrl(logoUrl),
                full: logoUrl,
                mono: true,
            },
            veevart: logoV2(record, logo),
            status: logo,
        },
    ];
}

/** Corner radius of the specimen: `sharp` is square, otherwise the SF radius (else 8px). */
function radiusOf(shape: string | null, radiusSf: string | null): number {
    if (shape === 'sharp') return 0;
    const px = /^(\d+(?:\.\d+)?)px$/.exec(radiusSf?.trim() ?? '');
    return px === null ? 8 : Math.min(Number(px[1]), 24);
}

/**
 * The brand as Veevart 2.0 holds it — drawn only once the run has migrated
 * the record, and only with what reached V2. A colour V2 refused, or a
 * typeface it did not apply, is drawn as V2's own default and said so: the
 * card never shows a Salesforce value V2 does not have.
 */
export function brandingSpecimen(record: FinalRecord, tenantName: string): BrandSpecimen | null {
    if (record.tracking?.status !== 'Migrated') return null;
    const c = record.columns;
    const colourKept = !warned(record, COLOR_NOT_MIGRATED);
    const buttonColor = colourKept ? swatchHex(c.colorV2) : null;
    const typeface = warned(record, FONT_NOT_APPLIED) ? null : text(c.fontV2);
    const shape = text(c.shapeV2);
    const logo = previewSummary(record.preview);
    return {
        kind: 'brand',
        name: tenantName,
        buttonColor,
        buttonText: readableTextOn(buttonColor),
        radiusPx: radiusOf(shape, text(c.radiusSf)),
        typeface,
        fontFamily: cssFontFamily(typeface),
        caption: [
            buttonColor === null
                ? 'Button colour: Veevart 2.0 default (no colour migrated).'
                : `Button colour ${buttonColor}.`,
            typeface === null
                ? 'Typeface not applied (default kept).'
                : `Typeface ${typeface} (shown only if your device has it).`,
            shape === null ? null : `${shape[0]?.toUpperCase() ?? ''}${shape.slice(1)} corners.`,
            logo === null ? 'No logo preview.' : `Logo: ${logo}.`,
        ]
            .filter((part): part is string => part !== null)
            .join(' '),
    };
}

function migratedRecords(sheets: readonly ClientSheet[]): FinalRecord[] {
    return sheets
        .map((sheet) => sheet.record)
        .filter((record): record is FinalRecord => record !== null);
}

function warningObservation(
    records: readonly FinalRecord[],
    code: string,
    label: string,
    lead: (count: number) => string,
): Observation | null {
    const hits = records.flatMap((record) =>
        (record.tracking?.warnings ?? []).filter((warning) => warning.code === code),
    );
    const first = hits[0];
    if (first === undefined) return null;
    return { label, code, text: `${lead(hits.length)} ${first.message}` };
}

export function brandingObservations(sheets: readonly ClientSheet[]): Observation[] {
    const records = migratedRecords(sheets);
    const payColours = records.filter((record) => swatchHex(record.columns.colorSfPay) !== null);
    const payment: Observation | null =
        payColours.length === 0
            ? null
            : {
                  label: 'Payment colour',
                  code: null,
                  text:
                      `${plural(payColours.length, 'client has', 'clients have')} a Salesforce payment button ` +
                      `colour (e.g. ${String(swatchHex(payColours[0]?.columns.colorSfPay))}). Veevart 2.0 ` +
                      'has no field for it: only the button colour becomes the brand colour.',
              };
    return [
        payment,
        warningObservation(
            records,
            FONT_NOT_APPLIED,
            'Typeface',
            (n) => `Mapped but not applied for ${plural(n, 'client')}.`,
        ),
        warningObservation(
            records,
            FONT_NOT_AVAILABLE,
            'Typeface',
            (n) => `No Google Fonts equivalent for ${plural(n, 'client')}.`,
        ),
        warningObservation(
            records,
            LOGO_NOT_MIGRATED,
            'Logo',
            (n) => `Not migrated for ${plural(n, 'client')}.`,
        ),
        warningObservation(
            records,
            COLOR_NOT_MIGRATED,
            'Button colour',
            (n) => `The default colour was used for ${plural(n, 'client')}.`,
        ),
    ].filter((observation): observation is Observation => observation !== null);
}

export const BRANDING_REPORT: ModuleReportView = {
    recordNoun: 'brand record',
    table: (record) => ({ kind: 'mapping', rows: brandingMappingRows(record) }),
    preview: brandingSpecimen,
    recordChip: (record) => {
        const state = text(record.columns.v2State);
        return state === null ? null : `Veevart 2.0 brand: ${state}`;
    },
    facts: (record) => {
        const primaries = record.columns.primaryCount;
        return typeof primaries === 'number'
            ? [{ label: 'Primary records', value: String(primaries) }]
            : [];
    },
    observations: brandingObservations,
    explainedWarningCodes: new Set([
        FONT_NOT_APPLIED,
        FONT_NOT_AVAILABLE,
        LOGO_NOT_MIGRATED,
        COLOR_NOT_MIGRATED,
    ]),
    warningTopic: (code) => TOPICS[code] ?? null,
    launchColumns: false,
};
